import { Avatar, AvatarSize, Chip, TableCell, theme, IconDictionary } from '@ketch-com/deck'
import { TrackerListItem, TrackerListItemOrigin } from '@ketch-com/windlass/dist/dataSentry/v1/trackers/trackers'
import { Box, Typography, Tooltip } from '@mui/material'
import { GridColDef, GridColumnHeaderParams, GridRenderCellParams } from '@mui/x-data-grid-premium'
import { avatarIconByOrigin, originCopy } from '../constants'
import pluralize from 'pluralize'
import { getSystemName } from 'pages/dataSentry/utils/getSystemName'
import { Dispatch, SetStateAction } from 'react'

export const dataSentryTrackersDataGridUtil = (
  trackerOverview: TrackerListItem | null = null,
  setTrackerOverview: Dispatch<SetStateAction<TrackerListItem | null>> = () => {},
): GridColDef<TrackerListItem>[] => {
  const columns: GridColDef<TrackerListItem>[] = [
    {
      align: 'left',
      field: 'name',
      headerName: 'Tracker',
      flex: 3,
      headerAlign: 'left',
      sortable: false,
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography variant="smallLabel" color={theme.palette.Text.Secondary}>
          {params.colDef.headerName}
        </Typography>
      ),
      renderCell: ({ row }: GridRenderCellParams<TrackerListItem>) => {
        return (
          <TableCell>
            <Box display="flex" alignItems="center" gap={1} maxWidth="100%">
              <Avatar
                icon={avatarIconByOrigin[row?.origin || TrackerListItemOrigin.TRACKER_ORIGIN_UNSPECIFIED]}
                textColor={theme.palette.Text.Secondary}
                variant="square"
                backgroundColor={theme.palette.Black.o8}
                size={AvatarSize.medium}
                sx={{ borderRadius: 1.5 }}
              />
              <Typography
                component="div"
                variant="label"
                sx={{
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  maxWidth: '100%',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  setTrackerOverview?.(row)
                }}
              >
                {row.name}
              </Typography>
            </Box>
          </TableCell>
        )
      },
    },
    {
      align: 'left',
      field: 'system',
      headerName: 'System',
      flex: 2,
      headerAlign: 'left',
      sortable: false,
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography variant="smallLabel" color={theme.palette.Text.Secondary}>
          {params.colDef.headerName}
        </Typography>
      ),
      renderCell: ({ row }: GridRenderCellParams<TrackerListItem>) => {
        return (
          <Box display="flex" gap={1} alignItems="center">
            {row.dataSystem?.logoUrl ? (
              <Avatar src={row.dataSystem?.logoUrl} alt={row.dataSystem?.name} size={AvatarSize.small} />
            ) : (
              <Avatar icon="NoLogo" size={AvatarSize.small} textColor={theme.palette.Black.o48} isLogo />
            )}
            <Typography variant="body">{getSystemName(row?.dataSystem || {})}</Typography>
          </Box>
        )
      },
    },
    {
      align: 'left',
      field: 'origin',
      headerName: 'Origin',
      flex: 2,
      headerAlign: 'left',
      sortable: false,
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography variant="smallLabel" color={theme.palette.Text.Secondary}>
          {params.colDef.headerName}
        </Typography>
      ),
      renderCell: ({ row }: GridRenderCellParams<TrackerListItem>) => {
        return (
          <TableCell>
            <Typography>{originCopy[row?.origin || TrackerListItemOrigin.TRACKER_ORIGIN_UNSPECIFIED]}</Typography>
          </TableCell>
        )
      },
    },
    {
      align: 'left',
      field: 'usheredBy',
      headerName: 'Ushered By',
      flex: 2,
      headerAlign: 'left',
      sortable: false,
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography variant="smallLabel" color={theme.palette.Text.Secondary}>
          {params.colDef.headerName}
        </Typography>
      ),
      renderCell: ({ row }: GridRenderCellParams<TrackerListItem>) => {
        const hasUsheredBy = (row?.usheredBy?.length || 0) > 0
        const usheredBy = row?.usheredBy

        return (
          <TableCell>
            {hasUsheredBy ? (
              <Box display="flex" gap={1} alignItems="center">
                <Tooltip
                  placement="bottom"
                  title={
                    <Box display="flex" flexDirection="column" gap={0.5} maxHeight={120} overflow="auto">
                      {usheredBy?.map(system => <Typography key={system?.code}>{getSystemName(system)}</Typography>)}
                    </Box>
                  }
                >
                  <Box>
                    {usheredBy?.length === 1 ? (
                      <Avatar
                        src={usheredBy?.[0]?.logoUrl || ''}
                        icon={usheredBy?.[0]?.logoUrl ? undefined : IconDictionary.NoLogo}
                        alt={usheredBy?.[0]?.name || ''}
                        textColor={usheredBy?.[0]?.logoUrl ? theme.palette.Black.o48 : theme.palette.Text.Secondary}
                        size={AvatarSize['small']}
                        isLogo
                      />
                    ) : (
                      <Chip label={usheredBy?.length} size="small" />
                    )}
                  </Box>
                </Tooltip>
              </Box>
            ) : (
              'None'
            )}
          </TableCell>
        )
      },
    },
    {
      align: 'left',
      field: 'purposes',
      headerName: 'Purposes',
      flex: 2,
      headerAlign: 'left',
      sortable: false,
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography variant="smallLabel" color={theme.palette.Text.Secondary}>
          {params.colDef.headerName}
        </Typography>
      ),
      renderCell: ({ row }: GridRenderCellParams<TrackerListItem>) => {
        return (
          <TableCell>
            {row?.purposes?.length ? (
              <Tooltip
                placement="bottom"
                title={
                  <Box display="flex" flexDirection="column" gap={0.5} maxHeight={120} overflow="auto">
                    {row?.purposes?.map(purpose => (
                      <Typography key={purpose?.code}>{purpose?.name || purpose?.code}</Typography>
                    ))}
                  </Box>
                }
              >
                <Box>
                  <Chip
                    size="small"
                    label={`${row.purposes?.length ? row.purposes.length : 0} ${pluralize(
                      'purpose',
                      row.purposes?.length === 1 ? 1 : 2,
                    )}`}
                  />
                </Box>
              </Tooltip>
            ) : (
              <Typography>Unknown</Typography>
            )}
          </TableCell>
        )
      },
    },
    {
      align: 'left',
      field: 'categories',
      headerName: 'Categories',
      flex: 2,
      headerAlign: 'left',
      sortable: false,
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography variant="smallLabel" color={theme.palette.Text.Secondary}>
          {params.colDef.headerName}
        </Typography>
      ),
      renderCell: ({ row }: GridRenderCellParams<TrackerListItem>) => {
        return (
          <TableCell>
            {row?.categories?.length ? (
              <Tooltip
                placement="bottom"
                title={
                  <Box display="flex" flexDirection="column" gap={0.5} maxHeight={120} overflow="auto">
                    {row?.categories?.map(category => (
                      <Typography key={category?.code}>{category?.name || category?.code}</Typography>
                    ))}
                  </Box>
                }
              >
                <Box>
                  <Chip
                    size="small"
                    label={`${row.categories?.length ? row.categories.length : 0} ${pluralize(
                      'category',
                      row.categories?.length === 1 ? 1 : 2,
                    )}`}
                  />
                </Box>
              </Tooltip>
            ) : (
              <Typography>Unknown</Typography>
            )}
          </TableCell>
        )
      },
    },
  ]

  return columns
}
